import { 
  Exercise as ComponentExercise, 
  ExerciseType as ApiExerciseType,
  ExerciseData as ApiExerciseData
} from "@/services/admin/exercises.interfaces";
import { LucideIcon } from "lucide-react";
import { 
  BookOpen, 
  Mic, 
  ListChecks, 
  FormInput, 
  MoveHorizontal, 
  Video
} from 'lucide-react';

/**
 * Enum unificado para tipos de exercícios
 * Consolida as diferentes definições de tipos de exercícios no sistema
 */
export enum ExerciseType {
  MultipleChoice = 'multiple_choice',
  FillInTheBlanks = 'fill_in_the_blanks',
  DragAndDrop = 'drag_and_drop',
  AudioRecording = 'audio_recording',
  Video = 'video',
  ComprehensionQuestion = 'comprehension_question',
  PronunciationPractice = 'pronunciation_practice',
  SpeakTheSentence = 'speak_the_sentence',
  WriteTheTranslation = 'write_the_translation',
  FillInTheText = 'fill_in_the_text',
  WatchVideo = 'watch_video',
  WatchLargeVideo = 'watch_large_video',
  FormattedText = 'formatted_text',
  StyledSection = 'styled_section',
  GlossaryDisplay = 'glossary_display'
}

/**
 * Mapeamento de tipos de API para tipos internos
 */
export const exerciseTypeMap: Record<ApiExerciseType, ExerciseType> = {
  [ApiExerciseType.MultipleChoice]: ExerciseType.MultipleChoice,
  [ApiExerciseType.FillInTheBlanks]: ExerciseType.FillInTheBlanks,
  [ApiExerciseType.DragAndDrop]: ExerciseType.DragAndDrop,
  [ApiExerciseType.AudioRecording]: ExerciseType.AudioRecording,
  [ApiExerciseType.Video]: ExerciseType.Video,
  [ApiExerciseType.ComprehensionQuestion]: ExerciseType.ComprehensionQuestion,
  [ApiExerciseType.PronunciationPractice]: ExerciseType.PronunciationPractice,
  [ApiExerciseType.SpeakTheSentence]: ExerciseType.SpeakTheSentence,
  [ApiExerciseType.WriteTheTranslation]: ExerciseType.WriteTheTranslation,
  [ApiExerciseType.WatchLargeVideo]: ExerciseType.WatchLargeVideo,
  [ApiExerciseType.FormattedText]: ExerciseType.FormattedText,
  [ApiExerciseType.StyledSection]: ExerciseType.StyledSection,
  [ApiExerciseType.GlossaryDisplay]: ExerciseType.GlossaryDisplay
};

/**
 * Mapeamento de tipos internos para tipos de API
 */
export const reverseExerciseTypeMap: Record<ExerciseType, ApiExerciseType> = {
  [ExerciseType.MultipleChoice]: ApiExerciseType.MultipleChoice,
  [ExerciseType.FillInTheBlanks]: ApiExerciseType.FillInTheBlanks,
  [ExerciseType.DragAndDrop]: ApiExerciseType.DragAndDrop,
  [ExerciseType.AudioRecording]: ApiExerciseType.AudioRecording,
  [ExerciseType.Video]: ApiExerciseType.Video,
  [ExerciseType.ComprehensionQuestion]: ApiExerciseType.ComprehensionQuestion,
  [ExerciseType.PronunciationPractice]: ApiExerciseType.PronunciationPractice,
  // Mapeamentos adicionais (estes podem precisar ser ajustados conforme a API)
  [ExerciseType.FillInTheText]: ApiExerciseType.FillInTheBlanks,
  [ExerciseType.WatchVideo]: ApiExerciseType.Video,
  [ExerciseType.FormattedText]: ApiExerciseType.FormattedText,
  [ExerciseType.StyledSection]: ApiExerciseType.StyledSection,
  [ExerciseType.GlossaryDisplay]: ApiExerciseType.GlossaryDisplay
};

/**
 * Mapeamento de ícones para tipos de exercícios
 */
export const exerciseIconMap: Record<ExerciseType, LucideIcon> = {
  [ExerciseType.MultipleChoice]: ListChecks,
  [ExerciseType.FillInTheBlanks]: FormInput,
  [ExerciseType.DragAndDrop]: MoveHorizontal,
  [ExerciseType.AudioRecording]: Mic,
  [ExerciseType.Video]: Video,
  [ExerciseType.ComprehensionQuestion]: BookOpen,
  [ExerciseType.PronunciationPractice]: Mic,
  [ExerciseType.SpeakTheSentence]: Mic,
  [ExerciseType.WriteTheTranslation]: FormInput,
  [ExerciseType.FillInTheText]: FormInput,
  [ExerciseType.WatchVideo]: Video,
  [ExerciseType.WatchLargeVideo]: Video,
  // Novos tipos de exercício
  [ExerciseType.FormattedText]: BookOpen,
  [ExerciseType.StyledSection]: ListChecks,
  [ExerciseType.GlossaryDisplay]: BookOpen
};

/**
 * Opções de tipos de exercícios para seletores de UI
 */
export const exerciseTypeOptions = [
  { value: ExerciseType.MultipleChoice, label: 'Múltipla Escolha' },
  { value: ExerciseType.FillInTheBlanks, label: 'Preencher Lacunas' },
  { value: ExerciseType.DragAndDrop, label: 'Arrastar e Soltar' },
  { value: ExerciseType.AudioRecording, label: 'Gravação de Áudio' },
  { value: ExerciseType.Video, label: 'Vídeo' },
  { value: ExerciseType.ComprehensionQuestion, label: 'Questão de Compreensão' },
  { value: ExerciseType.PronunciationPractice, label: 'Prática de Pronúncia' },
  { value: ExerciseType.SpeakTheSentence, label: 'Falar a Frase' },
  { value: ExerciseType.WriteTheTranslation, label: 'Escrever a Tradução' },
  { value: ExerciseType.FillInTheText, label: 'Preencher o Texto' },
  { value: ExerciseType.WatchVideo, label: 'Assistir Vídeo' },
  { value: ExerciseType.WatchLargeVideo, label: 'Assistir Vídeo Grande' },
  { value: ExerciseType.FormattedText, label: 'Texto Formatado' },
  { value: ExerciseType.StyledSection, label: 'Separador de Seção' },
  { value: ExerciseType.GlossaryDisplay, label: 'Glossário Interativo' }
];

/**
 * Tipo base para as informações de cartão de exercício
 */
export interface CardInfo {
  title: string;
  subtitle: string;
  description?: string;
}

/**
 * Interface para a função getEditorForType
 */
export interface EditorInterfaceStatic {
  GetCardInfo: (exercise: ComponentExercise) => CardInfo;
  getTitle: () => string;
  getDescription: (exercise: ComponentExercise) => string;
  getIcon: () => LucideIcon | null;
  SupportsExerciseType?: (type: string) => boolean;
}

/**
 * Tipo de dados para exercícios internos
 * Aliás para tornar mais fácil a referência ao tipo de dados da API
 */
export type ExerciseData = ApiExerciseData;